import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import { Cushion, Rectangle } from '@pitchero/react-ui';
import { withTranslation } from '../../lib/i18n';
import isServer from '../../lib/is-server';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import {
  fetchComments,
  fetchCommentPermissions,
  postComment,
  restoreComment as restoreAction,
  deleteComment as deleteAction,
} from '../../store/comments/actions';
import {
  canAdministerClubComments,
  canAddCommentsAtClub,
  isCheckingPermissions as isCheckingPermissionsSelector,
  isLoadingComments,
  hasLoadedComments,
  hasFailedToLoad,
  isSubmittingComment,
  getComments,
} from '../../store/comments/selectors';
import { isLoggedIn, loggedInUserId } from '../../store/auth/selectors';
import AccessDenied from '../auth/access-denied';
import LoadingBar from '../loading-bar';
import Comment from './comment';
import Form from './form';
import LoadingPlaceholder from './loading-placeholder';
import LoadingFailed from './loading-failed-state';
import NoCommentsPlaceholder from './no-comments-placeholder';

class Comments extends React.Component {
  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { mediaId, mediaType } = this.props;
    const { mediaId: prevMediaId, mediaType: prevMediaType } = prevProps;
    if (mediaId !== prevMediaId || mediaType !== prevMediaType) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const { previouslyLoaded, isLoading } = this.props;
    if (!previouslyLoaded && !isLoading) {
      this.load();
    }
  };

  onAuthoriseHandler = () => {
    const {
      club: { id: clubId },
      mediaId,
      mediaType,
      checkPermissions,
      isCheckingPermissions,
    } = this.props;
    if (!isCheckingPermissions) {
      checkPermissions(clubId, mediaType, mediaId);
    }
  };

  load = () => {
    const {
      club: { id: clubId },
      mediaId,
      mediaType,
      loadComments,
    } = this.props;
    loadComments(clubId, mediaType, mediaId);
  };

  addCommentHandler = (text, replyingTo) => {
    if (text.length) {
      const {
        addComment,
        club: { id: clubId },
        mediaId,
        mediaType,
      } = this.props;
      addComment(clubId, mediaType, mediaId, text, replyingTo);
    }
  };

  deleteCommentHandler = (commentId, parentId) => {
    const {
      deleteComment,
      club: { id: clubId },
      mediaId,
      mediaType,
    } = this.props;
    deleteComment(clubId, mediaType, mediaId, commentId, parentId);
  };

  restoreCommentHandler = (commentId, parentId) => {
    const {
      restoreComment,
      club: { id: clubId },
      mediaId,
      mediaType,
    } = this.props;
    restoreComment(clubId, mediaType, mediaId, commentId, parentId);
  };

  render() {
    const { club, isLoading, loadingFailed } = this.props;

    if (!club.commentsEnabled) {
      // Comments disabled at this club
      return null;
    }

    if (isLoading || isServer()) {
      return (
        <div>
          <LoadingPlaceholder />
          <LoadingPlaceholder />
          <LoadingPlaceholder />
        </div>
      );
    }

    if (loadingFailed) {
      return <LoadingFailed />;
    }

    const { canAdminister, canComment, comments, isCheckingPermissions, isSubmitting, userId } =
      this.props;
    const sortedComments = orderBy(comments, ['published'], ['desc']);

    const renderCommentForm = () => {
      if (canComment) {
        return (
          <Rectangle borderBottom="thinMercury">
            <Cushion bottom="medium" component="div">
              <Form onSubmit={this.addCommentHandler} isSubmitting={isSubmitting} />
            </Cushion>
          </Rectangle>
        );
      }

      if (isCheckingPermissions) {
        return <LoadingBar isLoading />;
      }

      return (
        <AccessDenied
          messageTranslationKey="common:comment_not_authorised"
          onAuthorise={this.onAuthoriseHandler}
          loginTranslationKey="common:login_to_comment"
          clubId={club.id}
        />
      );
    };

    return (
      <>
        {renderCommentForm()}
        {sortedComments.length === 0 && !isSubmitting ? (
          <NoCommentsPlaceholder />
        ) : (
          <>
            {isSubmitting && <LoadingPlaceholder />}
            {sortedComments.map((comment) => (
              <Comment
                key={comment.id}
                {...comment}
                canAdminister={canAdminister}
                userId={userId}
                canReply={canComment}
                onSubmitReply={this.addCommentHandler}
                onRestore={this.restoreCommentHandler}
                onDelete={this.deleteCommentHandler}
              />
            ))}
          </>
        )}
      </>
    );
  }
}

Comments.defaultProps = {
  userId: null,
};

Comments.propTypes = {
  addComment: PropTypes.func.isRequired,
  canAdminister: PropTypes.bool.isRequired,
  canComment: PropTypes.bool.isRequired,
  checkPermissions: PropTypes.func.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      text: PropTypes.string,
    }),
  ).isRequired,
  deleteComment: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isCheckingPermissions: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  loggedIn: PropTypes.bool.isRequired,
  loadComments: PropTypes.func.isRequired,
  loadingFailed: PropTypes.bool.isRequired,
  mediaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  mediaType: PropTypes.string.isRequired,
  previouslyLoaded: PropTypes.bool.isRequired,
  restoreComment: PropTypes.func.isRequired,
  userId: PropTypes.number,
};

const dispatchToProps = {
  addComment: postComment,
  checkPermissions: fetchCommentPermissions,
  deleteComment: deleteAction,
  loadComments: fetchComments,
  restoreComment: restoreAction,
};

const mapStateToProps = (state, ownProps) => ({
  loggedIn: isLoggedIn(state),
  isLoading: isLoadingComments(state, ownProps.club.id, ownProps.mediaType, ownProps.mediaId),
  isCheckingPermissions: isCheckingPermissionsSelector(state, ownProps.club.id),
  isSubmitting: isSubmittingComment(state, ownProps.club.id, ownProps.mediaType, ownProps.mediaId),
  canAdminister: canAdministerClubComments(
    state,
    ownProps.club.id,
    ownProps.mediaType,
    ownProps.mediaId,
  ),
  canComment: canAddCommentsAtClub(state, ownProps.club.id, ownProps.mediaType, ownProps.mediaId),
  checkPermissions: PropTypes.func.isRequired,
  comments: getComments(state, ownProps.club.id, ownProps.mediaType, ownProps.mediaId),
  previouslyLoaded: hasLoadedComments(
    state,
    ownProps.club.id,
    ownProps.mediaType,
    ownProps.mediaId,
  ),
  loadingFailed: hasFailedToLoad(state, ownProps.club.id, ownProps.mediaType, ownProps.mediaId),
  userId: loggedInUserId(state),
});

export default connect(mapStateToProps, dispatchToProps)(withTranslation(['common'])(Comments));
